"use client"

import { useState, useCallback, useRef } from "react"
import { Plus, X, Upload } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Progress } from "@/components/ui/progress"
import { uploadFileWithMetadata } from '@/api/upload'
import { formatFileSize } from '@/api/cloud'

// 格式化文件大小（字节转人类可读格式）
function formatFileSizeForQuota(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const k = 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + units[i]
}
import { useToast } from "@/hooks/use-toast"

interface CloudUploadDialogProps {
  open: boolean
  onClose: () => void
  onUploadComplete?: () => void // 上传完成后回调，用于刷新列表
}

interface UploadFileItem {
  file: File
  id: string
  status: 'pending' | 'uploading' | 'completed' | 'error'
  progress: number
  shareKey?: string
  downloadUrl?: string
  error?: string
}

export function CloudUploadDialog({ open, onClose, onUploadComplete }: CloudUploadDialogProps) {
  const [files, setFiles] = useState<UploadFileItem[]>([])
  const [expireDays, setExpireDays] = useState<string>("1")
  const [password, setPassword] = useState("")
  const [burnAfterRead, setBurnAfterRead] = useState(true) // 默认选中
  const [maxDownloads, setMaxDownloads] = useState<string>("1") // 默认1次
  const [description, setDescription] = useState("")
  const [agreed, setAgreed] = useState(true) // 默认选中
  const [isDragActive, setIsDragActive] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  // 处理文件选择（通用函数，支持input和拖拽）
  const handleFiles = useCallback((fileList: FileList | File[]) => {
    const filesArray = Array.isArray(fileList) ? fileList : Array.from(fileList)
    if (filesArray.length === 0) return

    const newFiles: UploadFileItem[] = filesArray.map((file) => ({
      file,
      id: `${Date.now()}-${Math.random()}`,
      status: 'pending' as const,
      progress: 0,
    }))

    setFiles((prev) => [...prev, ...newFiles])
    
    // 重置 input，允许重复选择同一文件
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  // 处理文件选择（input）
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
    if (!selectedFiles) return
    handleFiles(selectedFiles)
  }, [handleFiles])

  // 处理拖拽
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
    
    const droppedFiles = e.dataTransfer.files
    if (droppedFiles && droppedFiles.length > 0) {
      handleFiles(droppedFiles)
    }
  }, [handleFiles])

  // 移除文件
  const handleRemoveFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((item) => item.id !== id))
  }, [])

  // 检查存储容量
  const checkStorageCapacity = async (totalSize: number): Promise<boolean> => {
    try {
      const token = localStorage.getItem('accessToken')
      if (!token) {
        return true // 未登录用户跳过容量检查（由后端处理）
      }

      const response = await fetch('/api/v1/profile/storage-quota', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        const quota = data.data
        if (quota.freeStorage < totalSize) {
          toast({
            title: "存储空间不足",
            description: `需要 ${formatFileSizeForQuota(totalSize)}，剩余 ${quota.freeStorageHuman}（已使用 ${quota.usedStorageHuman} / ${quota.totalQuotaHuman}）`,
            variant: "destructive",
          })
          return false
        }
      }
      return true
    } catch (error) {
      console.error('检查存储容量失败:', error)
      // 检查失败时允许继续（后端会再次检查）
      return true
    }
  }

  // 开始上传
  const handleStartUpload = useCallback(async () => {
    if (files.length === 0) {
      toast({
        title: "请选择文件",
        variant: "destructive",
      })
      return
    }

    if (!agreed) {
      toast({
        title: "请同意用户协议",
        variant: "destructive",
      })
      return
    }

    // ✅ 检查存储容量
    const totalSize = files.reduce((sum, item) => sum + item.file.size, 0)
    const hasCapacity = await checkStorageCapacity(totalSize)
    if (!hasCapacity) {
      return
    }

    // 更新状态为上传中
    setFiles((prev) => prev.map((item) => ({ ...item, status: 'uploading' as const, progress: 0 })))

    let firstShareId: number | undefined = undefined

    // 逐个上传文件
    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i]
      
      try {
        const response = await uploadFileWithMetadata(fileItem.file, {
          shareId: firstShareId, // 后续文件使用第一个文件的 shareId
          expireDays: parseInt(expireDays) || 7,
          password: password || undefined,
          burnAfterRead,
          maxDownloads: parseInt(maxDownloads) || undefined,
          description: description || undefined,
          onProgress: (progress) => {
            // 更新当前文件的上传进度
            setFiles((prev) => 
              prev.map((item) => 
                item.id === fileItem.id 
                  ? { ...item, progress }
                  : item
              )
            )
          },
          onShareInfoReady: (shareKey, downloadUrl) => {
            // 更新文件信息
            setFiles((prev) => 
              prev.map((item) => 
                item.id === fileItem.id 
                  ? { ...item, shareKey, downloadUrl }
                  : item
              )
            )
          },
        })

        // 保存第一个文件的 shareId，后续文件使用
        if (i === 0 && response.data?.shareId) {
          firstShareId = response.data.shareId
        }

        // 更新文件信息（从响应中获取）
        if (response.data) {
          setFiles((prev) => 
            prev.map((item) => 
              item.id === fileItem.id 
                ? { 
                    ...item, 
                    status: 'completed' as const, 
                    progress: 100,
                    shareKey: response.data.shareKey || item.shareKey,
                    downloadUrl: response.data.downloadUrl || item.downloadUrl,
                  }
                : item
            )
          )
        }

        toast({
          title: "上传成功",
          description: `${fileItem.file.name} 上传完成`,
        })
      } catch (error: any) {
        // 上传失败
        setFiles((prev) => 
          prev.map((item) => 
            item.id === fileItem.id 
              ? { 
                  ...item, 
                  status: 'error' as const,
                  error: error.message || '上传失败'
                }
              : item
          )
        )

        toast({
          title: "上传失败",
          description: `${fileItem.file.name}: ${error.message || '上传失败'}`,
          variant: "destructive",
        })
      }
    }

    // 等待所有文件状态更新后，检查是否全部完成
    setTimeout(() => {
      setFiles((currentFiles) => {
        const allCompleted = currentFiles.every((item) => 
          item.status === 'completed' || item.status === 'error'
        )
        const allSuccess = currentFiles.every((item) => item.status === 'completed')

        if (allCompleted) {
          // 所有文件上传完成后，刷新列表
          if (onUploadComplete) {
            onUploadComplete()
          }

          // 如果所有文件都上传成功，延迟关闭弹窗
          if (allSuccess) {
            setTimeout(() => {
              // 重置状态并关闭
              setFiles([])
              setPassword("")
              setBurnAfterRead(true)
              setMaxDownloads("1")
              setDescription("")
              setAgreed(true)
              setIsDragActive(false)
              onClose()
            }, 800)
          }
        }

        return currentFiles
      })
    }, 300)
  }, [files, expireDays, password, burnAfterRead, maxDownloads, description, agreed, toast, onUploadComplete, onClose])

  // 重置对话框
  const handleClose = useCallback(() => {
    // 如果有正在上传的文件，提示用户
    const hasUploading = files.some((item) => item.status === 'uploading')
    if (hasUploading) {
      if (!confirm('有文件正在上传中，确定要关闭吗？')) {
        return
      }
    }

    // 重置状态
    setFiles([])
    setPassword("")
    setBurnAfterRead(true) // 重置为默认值
    setMaxDownloads("1") // 重置为默认值
    setDescription("")
    setAgreed(true) // 重置为默认值
    setIsDragActive(false)
    onClose()
  }, [files, onClose])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm max-h-[90vh] overflow-y-auto p-0 gap-0" showCloseButton={false}>
        <DialogHeader className="px-4 pt-4 pb-3 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-medium text-gray-900">新建文件</DialogTitle>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="px-4 pt-3 pb-4 space-y-3">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload-input"
          />

          {/* 文件列表（同时作为拖拽区域） */}
          <div
            className={`border-2 border-dashed rounded-lg transition-colors ${
              isDragActive 
                ? 'border-blue-500 bg-blue-50' 
                : files.length > 0
                  ? 'border-gray-200 bg-gray-50'
                  : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => {
              if (files.length === 0) {
                fileInputRef.current?.click()
              }
            }}
            style={{ cursor: files.length === 0 ? 'pointer' : 'default' }}
          >
            {files.length > 0 ? (
              <div className="p-2 max-h-48 overflow-y-auto space-y-1">
                {files.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2 p-2 bg-white rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
                        <p 
                          className="text-sm text-gray-900 truncate flex-1 min-w-0 max-w-[200px]" 
                          title={item.file.name}
                        >
                          {item.file.name}
                        </p>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {formatFileSize(item.file.size)}
                        </span>
                      </div>
                      {item.status === 'uploading' && (
                        <>
                          <p className="text-xs text-gray-500 ml-3.5 mt-0.5">
                            上传中 {item.progress}%
                          </p>
                          <Progress value={item.progress} className="mt-1 h-1 ml-3.5" />
                        </>
                      )}
                      {item.status === 'completed' && (
                        <p className="text-xs text-green-600 ml-3.5 mt-0.5">已完成</p>
                      )}
                      {item.status === 'error' && (
                        <p className="text-xs text-red-600 ml-3.5 mt-0.5">
                          失败: {item.error}
                        </p>
                      )}
                    </div>
                    {item.status !== 'uploading' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-gray-400 hover:text-gray-900 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveFile(item.id)
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-500 text-center pt-1 border-t border-gray-200 mt-1">
                  共 {formatFileSize(files.reduce((sum, item) => sum + item.file.size, 0))}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 p-6 text-center min-h-[120px]">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <Plus className="w-4 h-4 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900">
                  拖拽文件到此处或
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      fileInputRef.current?.click()
                    }}
                    className="text-blue-600 hover:text-blue-700 underline ml-1"
                  >
                    点击选择
                  </button>
                </p>
                <p className="text-xs text-gray-500">支持选择多个文件</p>
              </div>
            )}
          </div>

          {/* 有效期 */}
          <div className="space-y-1">
            <label className="text-xs text-gray-700 font-medium">有效期</label>
            <Select value={expireDays} onValueChange={setExpireDays}>
              <SelectTrigger className="w-full bg-white border-gray-200 rounded-lg h-8 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 天</SelectItem>
                <SelectItem value="3">3 天</SelectItem>
                <SelectItem value="7">7 天</SelectItem>
                <SelectItem value="30">30 天</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* 分享密码 */}
          <div className="space-y-1">
            <label className="text-xs text-gray-700 font-medium">分享密码（可选）</label>
            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg h-8">
              {password ? (
                <span className="text-xs text-gray-900">{password}</span>
              ) : (
                <span className="text-xs text-gray-500">无</span>
              )}
            </div>
          </div>

          {/* 最大下载次数 */}
          <div className="space-y-1">
            <label className="text-xs text-gray-700 font-medium">最大下载次数（可选）</label>
            <Input
              type="number"
              placeholder="0 表示不限制"
              value={maxDownloads}
              onChange={(e) => setMaxDownloads(e.target.value)}
              className="h-8 text-sm"
              min="0"
            />
          </div>

          {/* 下载后销毁 */}
          <div className="flex items-center gap-2 pt-0.5">
            <Checkbox
              id="burn-after-read"
              checked={burnAfterRead}
              onCheckedChange={(checked) => setBurnAfterRead(checked === true)}
              className="h-3.5 w-3.5"
            />
            <label htmlFor="burn-after-read" className="text-sm text-gray-700 cursor-pointer">
              下载后销毁
            </label>
          </div>

          {/* 备注 */}
          <div className="space-y-1">
            <label className="text-xs text-gray-700 font-medium">备注（可选）</label>
            <Input
              type="text"
              placeholder="添加备注信息"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-8 text-sm"
            />
          </div>

          {/* 用户协议 */}
          <div className="flex items-start gap-1.5 pt-0.5">
            <Checkbox
              id="upload-terms"
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              className="h-3.5 w-3.5 mt-0.5"
            />
            <label htmlFor="upload-terms" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
              已同意使用作品公约以及《使用协议》和《隐私政策》等平台相关规则指引
            </label>
          </div>

          {/* 开始上传按钮 */}
          <Button
            className="w-full h-9 bg-yellow-400 hover:bg-yellow-500 text-gray-900 rounded-full font-medium text-sm shadow-sm disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            onClick={handleStartUpload}
            disabled={files.length === 0 || !agreed || files.some((item) => item.status === 'uploading')}
          >
            {files.some((item) => item.status === 'uploading') ? (
              <>
                <Upload className="w-4 h-4 mr-2 animate-spin" />
                上传中...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                开始上传
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

